import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, ArrowLeft, Sparkles, Send, FileSpreadsheet, Paperclip, Link2, Phone, Mail,
  MapPin, CalendarClock, User, Gavel, Pencil, BadgeCheck, FileText, FileCode2, Trophy, Info,
  AlertTriangle, CheckCircle2, XCircle, Undo2, X, ArrowRight,
} from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { money, shortDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { RfqTypeBadge } from "@/components/RfqType";
import { PriceAutomaticallyButton } from "@/components/PriceAutomaticallyButton";
import { toast } from "sonner";

const grab = (url, filename) => {
  const token = localStorage.getItem("matiqs_token");
  fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
    .then((b) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch(() => toast.error("Could not download that file"));
};

const Fact = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
      <div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
        <div className="text-slate-900 dark:text-slate-100">{value}</div>
      </div>
    </div>
  ) : null;

export default function IncomingRfq() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState("");
  const [catEmpty, setCatEmpty] = useState(false);
  const [einv, setEinv] = useState(null);
  const [cn, setCn] = useState(null);
  const [vcheck, setVcheck] = useState(null);
  const [cnReason, setCnReason] = useState("");
  const [showCnBox, setShowCnBox] = useState(false);
  const [po, setPo] = useState(null);
  const [poRef, setPoRef] = useState("");
  const [invLines, setInvLines] = useState([]);
  const poFileRef = useRef(null);
  const createdQuoteId = useRef(null);

  const ensureQuoteId = async () => {
    if (o?.quote_rfq_id) { createdQuoteId.current = o.quote_rfq_id; return o.quote_rfq_id; }
    const { data } = await api.post(`/vendor/opportunities/${id}/start`, null, { params: { mode: "manual" } });
    createdQuoteId.current = data.rfq_id;
    return data.rfq_id;
  };

  const onPricedFromList = () => {
    if (createdQuoteId.current) navigate(`/rfq/${createdQuoteId.current}`);
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/vendor/opportunities/${id}`);
      setO(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "That RFQ is not available");
      navigate("/tracker");
    }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/catalogue").then((r) => setCatEmpty((r.data || []).length === 0)).catch(() => {});
  }, []);
  const loadEinvoice = async () => {
    const { data } = await api.get(`/vendor/rfqs/${id}/einvoice`);
    setEinv(data);
    setPo(data.po || { requested: false, received: false });
    setInvLines((data.lines || []).map((l) => ({ ...l })));
    if (data.exists) {
      api.get(`/vendor/rfqs/${id}/einvoice/validate`).then((x) => setVcheck(x.data)).catch(() => {});
      api.get(`/vendor/rfqs/${id}/credit-note`).then((x) => setCn(x.data)).catch(() => {});
    }
  };
  useEffect(() => {
    if (o?.awarded_to_me) loadEinvoice().catch(() => {});
  }, [o?.awarded_to_me, id]);

  const requestPo = async () => {
    setBusy("po-request");
    try {
      const { data } = await api.post(`/vendor/rfqs/${id}/po/request`, {});
      toast.success(data.emailed ? "Request sent to the contractor" : "Marked as requested");
      await loadEinvoice();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send the request");
    } finally { setBusy(""); }
  };

  const markPoReceived = async () => {
    setBusy("po-received");
    try {
      const fd = new FormData();
      fd.append("reference", poRef || "");
      const f = poFileRef.current?.files?.[0];
      if (f) fd.append("file", f);
      await api.post(`/vendor/rfqs/${id}/po/received`, fd);
      toast.success("Purchase Order recorded — you can now invoice");
      if (poFileRef.current) poFileRef.current.value = "";
      await loadEinvoice();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not record the Purchase Order");
    } finally { setBusy(""); }
  };

  const updateLine = (i, field, value) =>
    setInvLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const addLine = () => setInvLines((prev) => [...prev, { description: "", unit: "", qty: 1, rate: 0 }]);
  const removeLine = (i) => setInvLines((prev) => prev.filter((_, idx) => idx !== i));

  const generateEinvoice = async () => {
    setBusy("einvoice");
    try {
      const lines = invLines
        .map((l) => ({ description: (l.description || "").trim(), unit: l.unit || "",
                       qty: parseFloat(l.qty) || 0, rate: parseFloat(l.rate) || 0 }))
        .filter((l) => l.description || l.qty || l.rate);
      const { data } = await api.post(`/vendor/rfqs/${id}/einvoice`, { lines });
      toast.success(`E-invoice ${data.invoice_number} ready`);
      (data.errors || []).forEach((w) => toast.error(w));
      (data.warnings || []).forEach((w) => toast.warning(w));
      setVcheck({ errors: data.errors || [], warnings: data.warnings || [] });
      await loadEinvoice();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not generate the e-invoice");
    } finally {
      setBusy("");
    }
  };

  const createCreditNote = async () => {
    if (!cnReason.trim()) { toast.error("Please enter a reason for the credit note"); return; }
    setBusy("creditnote");
    try {
      const { data } = await api.post(`/vendor/rfqs/${id}/credit-note`, { reason: cnReason.trim() });
      toast.success(`Credit note ${data.credit_note_number} created`);
      const r = await api.get(`/vendor/rfqs/${id}/credit-note`);
      setCn(r.data);
      setShowCnBox(false);
      setCnReason("");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not create the credit note");
    } finally {
      setBusy("");
    }
  };

  const startQuote = async (mode) => {
    setBusy(mode === "manual" ? "manual" : "price");
    try {
      const { data } = await api.post(`/vendor/opportunities/${id}/start`, null, { params: { mode } });
      if (data.manual) {
        toast.success("Blank quotation ready — fill in the rates yourself");
      } else if (data.ai_skipped) {
        toast.warning(`Items loaded without AI pricing — your ${data.plan_name} plan's ${data.quota} automatic quotes are used up this month`);
      } else if (!data.reused) {
        toast.success(`Priced ${data.matched} of ${data.total} lines from your catalogue`);
      }
      navigate(`/rfq/${data.rfq_id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not start the quotation");
    } finally {
      setBusy("");
    }
  };

  const sendQuote = async () => {
    setBusy("send");
    try {
      const { data } = await api.post(`/vendor/opportunities/${id}/submit`);
      toast.success(`Quotation sent to ${o.buyer_company} — ${o.currency} ${Number(data.total).toLocaleString()}`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not send the quotation");
    } finally {
      setBusy("");
    }
  };

  if (!o) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const items = o.items || [];

  return (
    <div data-testid="incoming-rfq-page" className="space-y-6">
      <button onClick={() => navigate("/tracker")} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to tracker
      </button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Incoming RFQ</div>
            <RfqTypeBadge type={o.rfq_type} />
            {o.buyer_verified && (
              <span data-testid="buyer-verified-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 text-[11px] font-bold">
                <BadgeCheck className="w-3 h-3" /> Verified contractor
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mt-2">{o.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            <span className="font-mono">{o.rfq_ref || "—"}</span> · from {o.buyer_company} · received {shortDate(o.invited_at)} · {o.item_count} priced lines
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {o.quote_rfq_id ? (
            <>
              <Button variant="outline" data-testid="open-my-quote-button" onClick={() => navigate(`/rfq/${o.quote_rfq_id}`)} className="h-11 font-semibold">
                Open my quotation
              </Button>
              <Button data-testid="send-quote-button" onClick={sendQuote} disabled={busy === "send"} className="h-11 gap-2 font-semibold">
                {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {o.my_status === "quoted" ? "Resend quotation" : "Send quotation"}
              </Button>
            </>
          ) : o.item_count > 0 ? (
            <div className="flex flex-col items-end gap-1.5">
              <div className="flex items-center gap-2">
                <Button variant="outline" data-testid="price-manually-button" onClick={() => startQuote("manual")} disabled={!!busy} className="h-11 gap-2 font-semibold">
                  {busy === "manual" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />}
                  Price manually
                </Button>
                <PriceAutomaticallyButton
                  testid="price-it-button"
                  className="h-11 gap-2 font-semibold"
                  catEmpty={catEmpty}
                  getRfqId={ensureQuoteId}
                  onCatalogue={async () => { await startQuote("auto"); }}
                  onPriced={onPricedFromList}
                />
              </div>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">No readable BOQ — quote this one by email</span>
          )}
        </div>
      </div>

      {o.bafo && o.bafo.status === "requested" && (
        <div data-testid="bafo-banner" className="rounded-2xl border border-brand/40 bg-brand-soft/50 px-5 py-4">
          <div className="flex items-center gap-2 font-bold text-brand">
            <Gavel className="w-4 h-4" /> Best and final price requested
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {o.buyer_company} has asked you to sharpen your price
            {o.bafo.deadline ? ` by ${o.bafo.deadline}` : ""}
            {o.bafo.previous_total ? ` — your current submission is ${o.currency} ${money(o.bafo.previous_total, "").trim()}` : ""}.
            Open your quotation, adjust the rates and resend.
          </p>
          {o.bafo.message && <p className="text-sm italic mt-2">“{o.bafo.message}”</p>}
        </div>
      )}

      {o.awarded_to_me && (
        <div data-testid="awarded-card" className="rounded-2xl border border-emerald-300 dark:border-emerald-900 bg-emerald-50/70 dark:bg-emerald-950/30 px-5 py-5">
          <div className="flex items-center gap-2 font-bold text-emerald-800 dark:text-emerald-200">
            <Trophy className="w-4 h-4" /> Quotation awarded — {o.buyer_company} accepted your quotation
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Once you receive the Purchase Order / contract, raise your UAE FTA-compliant tax invoices in the
            Matiqs Qonnect — upload the contract BOQ and invoice each payment certificate.
          </p>
          <a href="/einvoicing/app" data-testid="go-einvoicing-link"
             className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-primary hover:underline">
            Open Matiqs Qonnect <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      )}

      {false && o.awarded_to_me && (
        <div data-testid="einvoice-card" className="rounded-2xl border border-emerald-300 dark:border-emerald-900 bg-emerald-50/70 dark:bg-emerald-950/30 px-5 py-5">
          <div className="flex items-center gap-2 font-bold text-emerald-800 dark:text-emerald-200">
            <Trophy className="w-4 h-4" /> Quotation awarded — {o.buyer_company} accepted your quotation
          </div>

          {!po?.received ? (
            <>
              <p className="text-sm text-muted-foreground mt-1">
                Before you raise a tax invoice, request the <b>Purchase Order / Agreement / Contract</b> from
                the contractor. Once you've received it, record it below to unlock e-invoicing.
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <Button data-testid="request-po-button" onClick={requestPo} disabled={busy === "po-request"}
                        variant={po?.requested ? "outline" : "default"} className="h-11 gap-2 font-semibold">
                  {busy === "po-request" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {po?.requested ? "Request again" : "Request Purchase Order"}
                </Button>
                {po?.requested && (
                  <span data-testid="po-requested-badge" className="text-sm text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Requested — we emailed the contractor
                  </span>
                )}
              </div>

              <div data-testid="po-receive-box" className="mt-4 rounded-xl border border-border bg-card px-4 py-4">
                <div className="font-semibold text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" /> Record Purchase Order / Contract received
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Enter a reference and optionally attach the PO/contract. This unlocks the e-invoice.
                </p>
                <div className="flex flex-wrap items-end gap-3 mt-3">
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">PO / Contract reference (optional)</label>
                    <input data-testid="po-reference-input" value={poRef} onChange={(e) => setPoRef(e.target.value)}
                      placeholder="e.g. PO-2026-00123" className="mt-1 w-full h-10 text-sm rounded-md border border-input bg-background px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                  </div>
                  <input ref={poFileRef} type="file" data-testid="po-file-input" className="text-sm max-w-[220px]"
                         accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" />
                  <Button data-testid="mark-po-received-button" onClick={markPoReceived} disabled={busy === "po-received"}
                          className="h-10 gap-2 font-semibold">
                    {busy === "po-received" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Mark as received
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mt-1">
                <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 font-semibold">
                  <CheckCircle2 className="w-4 h-4" /> Purchase Order recorded
                </span>
                {po?.reference ? ` · ${po.reference}` : ""}
                {po?.has_file ? " · " : ""}
                {po?.has_file && (
                  <button data-testid="po-file-download" onClick={() => grab(`/vendor/rfqs/${id}/po/file`, "purchase-order")}
                          className="text-brand hover:underline">view document</button>
                )}
                . Adjust the line items below to match the final agreement, then generate your Peppol PINT-AE tax invoice (5% VAT).
              </p>

              <div data-testid="invoice-lines-editor" className="mt-4 rounded-xl border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-accent text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2 font-bold">Description</th>
                      <th className="px-3 py-2 font-bold w-20">Unit</th>
                      <th className="px-3 py-2 font-bold w-20 text-right">Qty</th>
                      <th className="px-3 py-2 font-bold w-24 text-right">Rate</th>
                      <th className="px-3 py-2 font-bold w-28 text-right">Amount</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invLines.map((l, i) => (
                      <tr key={i} data-testid={`inv-line-${i}`} className="border-t border-border">
                        <td className="px-2 py-1.5">
                          <input data-testid={`inv-line-desc-${i}`} value={l.description} onChange={(e) => updateLine(i, "description", e.target.value)}
                                 className="w-full h-9 text-sm rounded-md border border-input bg-background px-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input data-testid={`inv-line-unit-${i}`} value={l.unit} onChange={(e) => updateLine(i, "unit", e.target.value)}
                                 className="w-full h-9 text-sm rounded-md border border-input bg-background px-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input data-testid={`inv-line-qty-${i}`} type="number" step="any" value={l.qty} onChange={(e) => updateLine(i, "qty", e.target.value)}
                                 className="w-full h-9 text-sm text-right rounded-md border border-input bg-background px-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input data-testid={`inv-line-rate-${i}`} type="number" step="any" value={l.rate} onChange={(e) => updateLine(i, "rate", e.target.value)}
                                 className="w-full h-9 text-sm text-right rounded-md border border-input bg-background px-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                          {((parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <button data-testid={`inv-line-remove-${i}`} onClick={() => removeLine(i)}
                                  className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-between px-3 py-2 border-t border-border">
                  <button data-testid="inv-line-add" onClick={addLine} className="text-sm font-semibold text-brand hover:underline">+ Add line</button>
                  <div className="text-sm text-muted-foreground">
                    Subtotal{" "}
                    <span className="font-mono font-bold text-slate-900 dark:text-white">
                      {(einv?.currency || o.currency)} {invLines.reduce((s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.rate) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                    {" "}· +5% VAT on generate
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <Button data-testid="generate-einvoice-button" onClick={generateEinvoice} disabled={busy === "einvoice"}
                        className="h-11 gap-2 font-semibold">
                  {busy === "einvoice" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {einv?.exists ? "Regenerate e-invoice" : "Generate e-invoice"}
                </Button>
                {einv?.exists && (
                  <>
                    <Button variant="outline" data-testid="download-einvoice-pdf" onClick={() => grab(`/vendor/rfqs/${id}/einvoice/download?fmt=pdf`, `${einv.invoice_number}.pdf`)} className="h-11 gap-2 font-semibold">
                      <FileText className="w-4 h-4" /> Download PDF
                    </Button>
                    <Button variant="outline" data-testid="download-einvoice-xml" onClick={() => grab(`/vendor/rfqs/${id}/einvoice/download?fmt=xml`, `${einv.invoice_number}.xml`)} className="h-11 gap-2 font-semibold">
                      <FileCode2 className="w-4 h-4" /> Download XML
                    </Button>
                  </>
                )}
              </div>
            </>
          )}

          {einv?.exists && (
            <div className="text-sm text-slate-700 dark:text-slate-200 mt-3">
              Invoice <span className="font-mono font-bold">{einv.invoice_number}</span> ·
              {" "}{einv.currency} {Number(einv.total).toLocaleString(undefined, { minimumFractionDigits: 2 })} ·
              {" "}issued {einv.issue_date}
            </div>
          )}

          {einv?.exists && vcheck && (
            <div data-testid="einvoice-validation" className="mt-3 rounded-lg border border-border bg-card px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">
                Pre-send check (PINT-AE)
              </div>
              {(vcheck.errors?.length || 0) === 0 && (vcheck.warnings?.length || 0) === 0 ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="w-4 h-4" /> All mandatory fields look good. Ready for your ASP.
                </div>
              ) : (
                <ul className="space-y-1">
                  {(vcheck.errors || []).map((e, i) => (
                    <li key={`e${i}`} className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
                      <XCircle className="w-4 h-4 shrink-0 mt-0.5" /> {e}
                    </li>
                  ))}
                  {(vcheck.warnings || []).map((w, i) => (
                    <li key={`w${i}`} className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {w}
                    </li>
                  ))}
                </ul>
              )}
              {(vcheck.errors?.length || 0) > 0 && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  You can still download, but your ASP will likely reject the file until these are fixed.
                </p>
              )}
            </div>
          )}

          {einv?.exists && (
            <div className="mt-4 border-t border-emerald-200 dark:border-emerald-900 pt-4">
              <div className="flex items-center gap-2 font-semibold text-sm text-slate-800 dark:text-slate-100">
                <Undo2 className="w-4 h-4" /> Credit note
                <span className="text-xs font-normal text-muted-foreground">(for a cancellation or refund)</span>
              </div>
              {cn?.exists ? (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="text-sm text-slate-700 dark:text-slate-200">
                    <span className="font-mono font-bold">{cn.credit_note_number}</span> against {cn.against} ·
                    {" "}{cn.currency} {Number(cn.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                  <Button variant="outline" size="sm" data-testid="download-cn-pdf" onClick={() => grab(`/vendor/rfqs/${id}/credit-note/download?fmt=pdf`, `${cn.credit_note_number}.pdf`)} className="gap-1.5">
                    <FileText className="w-3.5 h-3.5" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" data-testid="download-cn-xml" onClick={() => grab(`/vendor/rfqs/${id}/credit-note/download?fmt=xml`, `${cn.credit_note_number}.xml`)} className="gap-1.5">
                    <FileCode2 className="w-3.5 h-3.5" /> XML
                  </Button>
                </div>
              ) : showCnBox ? (
                <div className="mt-2 space-y-2">
                  <textarea data-testid="cn-reason-input" value={cnReason} onChange={(e) => setCnReason(e.target.value)}
                    rows={2} placeholder="Reason for the credit note (e.g. order cancelled by client)"
                    className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                  <div className="flex gap-2">
                    <Button size="sm" data-testid="confirm-cn-button" onClick={createCreditNote} disabled={busy === "creditnote"} className="gap-1.5">
                      {busy === "creditnote" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                      Create credit note
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setShowCnBox(false); setCnReason(""); }}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" data-testid="create-cn-button" onClick={() => setShowCnBox(true)} className="mt-2 gap-1.5">
                  <Undo2 className="w-3.5 h-3.5" /> Create credit note
                </Button>
              )}
            </div>
          )}

          <div className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300 bg-amber-100/70 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg px-3 py-2 mt-4">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <span data-testid="einvoice-asp-note">
              The XML file must be submitted to your Accredited Service Provider (ASP) for FTA clearance.
              Matiqs generates the compliant document and acts as Corner 1; it is not an ASP and does not itself transmit to the FTA.
            </span>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-6 space-y-5">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">Enquiry details</div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Fact icon={MapPin} label="Project" value={[o.project, o.location].filter(Boolean).join(" · ")} />
            <Fact icon={CalendarClock} label="Quotes due" value={o.due_date} />
            <Fact icon={User} label="Contact" value={[o.contact_person, o.contact_designation].filter(Boolean).join(" — ")} />
            <Fact icon={Phone} label="Phone" value={o.contact_phone} />
            <Fact icon={Mail} label="Email" value={o.contact_email} />
          </div>
          {o.notes && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Scope notes</div>
              <p className="text-sm whitespace-pre-line">{o.notes}</p>
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-4">Files</div>
          <div className="space-y-2">
            {o.boq && (
              <button data-testid="download-boq-button" onClick={() => grab(`/buyer/rfqs/${o.id}/boq/file`, o.boq.filename)}
                      className="w-full flex items-center gap-2 text-sm text-left px-3 py-2.5 rounded-xl border border-border hover:border-primary/40 transition-colors">
                <FileSpreadsheet className="w-4 h-4 text-primary shrink-0" />
                <span className="truncate">{o.boq.filename}</span>
              </button>
            )}
            {(o.attachments || []).map((a, j) => (
              <button key={a.id} data-testid={`download-att-${j}`} onClick={() => grab(`/buyer/rfqs/${o.id}/attachments/${a.id}/file`, a.filename)}
                      className="w-full flex items-center gap-2 text-sm text-left px-3 py-2.5 rounded-xl border border-border hover:border-primary/40 transition-colors">
                <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="truncate">{a.filename}</span>
              </button>
            ))}
            {o.package_url && (
              <a href={o.package_url} target="_blank" rel="noreferrer" data-testid="package-link"
                 className="w-full flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl border border-border hover:border-primary/40 transition-colors">
                <Link2 className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="truncate">Full package download</span>
              </a>
            )}
            {!o.boq && !(o.attachments || []).length && !o.package_url && (
              <p className="text-sm text-muted-foreground">No files were attached.</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <div className="font-semibold text-slate-900 dark:text-slate-100">Bill of quantities</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {items.length ? "Exactly as the contractor sent it — headers, tabs and quantities preserved." : "This RFQ came as a file only, so there are no readable lines."}
          </div>
        </div>
        {items.length > 0 && (
          <div className="overflow-x-auto max-h-[60vh]">
            <table data-testid="incoming-items-table" className="w-full text-sm">
              <thead className="sticky top-0">
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground bg-secondary">
                  <th className="text-left font-bold px-4 py-3 w-8">#</th>
                  <th className="text-left font-bold px-4 py-3 min-w-[280px]">Description</th>
                  <th className="text-left font-bold px-3 py-3">Ref</th>
                  <th className="text-left font-bold px-3 py-3">Unit</th>
                  <th className="text-right font-bold px-3 py-3">Qty</th>
                  <th className="text-left font-bold px-3 py-3 min-w-[140px]">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let n = 0;
                  return items.map((it, i) => {
                    if (it.kind !== "item") {
                      return (
                        <tr key={it.id} className="border-t border-border bg-secondary/40">
                          <td className="px-4 py-2" />
                          <td colSpan={5} className="px-4 py-2 font-bold text-slate-900 dark:text-slate-100"
                              style={{ paddingLeft: `${16 + (it.level || 0) * 16}px` }}>
                            {it.description}
                          </td>
                        </tr>
                      );
                    }
                    n += 1;
                    return (
                      <tr key={it.id} data-testid={`incoming-item-${i}`} className="border-t border-border">
                        <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono">{n}</td>
                        <td className="px-4 py-2.5" style={{ paddingLeft: `${16 + (it.level || 0) * 16}px` }}>{it.description}</td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground font-mono">{it.serial || it.sheet || "—"}</td>
                        <td className="px-3 py-2.5 text-muted-foreground">{it.unit || "—"}</td>
                        <td className="px-3 py-2.5 text-right font-mono">{Number(it.qty || 0).toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">{it.remarks || "—"}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
